import { useState, useEffect, useRef } from "react";
import { MapPin, Phone, Mail, Clock, Send, Building2, User, Package, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/context/AuthContext";

const contactInfo = {
  domestic: { label: "India Office", phone: ["+91 9666195695", "+91 7013696728"], email: "siddardh3.v@gmail.com", address: "S Rayavaram Mandal, 17/1, Koruprolu, Andhra Pradesh 531083, India", hours: "Monday - Saturday: 9:00 AM - 6:00 PM IST" },
  international: { label: "Export Office", phone: ["+91 9666195695", "+91 7013696728"], email: "siddardh3.v@gmail.com", address: "S Rayavaram Mandal, 17/1, Koruprolu, Andhra Pradesh 531083, India", hours: "Monday - Saturday: 9:00 AM - 6:00 PM IST (GMT+5:30)" },
};

export default function Contact() {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  const [type, setType] = useState("domestic");
  const [submitting, setSubmitting] = useState(false);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ market_type: "domestic", company_name: "", contact_name: "", email: "", phone: "", country: "India", products: [], quantity: "", delivery_port: "", payment_terms: "", message: "" });

  useEffect(() => {
    axios.get(`${API}/products`).then(r => setProducts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const toggleProduct = (name) => {
    setForm(f => ({ ...f, products: f.products.includes(name) ? f.products.filter(p => p !== name) : [...f.products, name] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Enquiry submitted successfully! We will contact you soon.");
      setForm({ market_type: "domestic", company_name: "", contact_name: "", email: "", phone: "", country: "India", products: [], quantity: "", delivery_port: "", payment_terms: "", message: "" });
    } catch (err) {
      toast.error("Failed to submit enquiry. Please try again.");
    }
    setSubmitting(false);
  };

  const info = contactInfo[type];

  return (
    <section ref={ref} className="section-padding bg-[#F9FAFB]" data-testid="contact-section">
      <div className="container-custom">
        <div className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-8 h-[3px] bg-forest" />
            <span className="text-forest font-medium text-sm uppercase tracking-widest font-sans">Contact Us</span>
            <div className="w-8 h-[3px] bg-forest" />
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-navy mb-4 tracking-tight">Get in <span className="text-forest">Touch</span></h2>
          <p className="text-slate-500 text-lg font-sans">Have a question or need a quote? Reach out and we'll respond within 24 hours.</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className={`transition-all duration-700 delay-200 ${vis ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <div className="bg-white p-2 mb-6 shadow-md">
              <div className="flex">
                {["domestic", "international"].map(t => (
                  <button key={t} onClick={() => { setType(t); setForm(f => ({ ...f, market_type: t, country: t === "domestic" ? "India" : "" })); }}
                    className={`flex-1 py-2 px-4 text-sm font-medium font-sans transition-all ${type === t ? "bg-navy text-white" : "text-slate-500 hover:bg-slate-50"}`} data-testid={`contact-type-${t}`}>
                    {t === "domestic" ? "Domestic (India)" : "International"}
                  </button>
                ))}
              </div>
            </div>
            <div className="bg-white shadow-md p-6 space-y-6">
              <h3 className="text-xl font-serif text-navy">{info.label}</h3>
              {[
                { icon: MapPin, label: "Address", value: info.address },
                { icon: Phone, label: "Phone", value: info.phone },
                { icon: Mail, label: "Email", value: [info.email] },
                { icon: Clock, label: "Working Hours", value: [info.hours] },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-forest/10 flex items-center justify-center flex-shrink-0"><item.icon className="w-5 h-5 text-forest" /></div>
                  <div>
                    <p className="font-medium text-navy text-sm font-sans">{item.label}</p>
                    {Array.isArray(item.value) ? item.value.map((v, j) => <p key={j} className="text-slate-500 text-sm font-sans">{v}</p>) : <p className="text-slate-500 text-sm font-sans">{item.value}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={`lg:col-span-2 transition-all duration-700 delay-400 ${vis ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            <div className="bg-white shadow-md p-6 md:p-8">
              <h3 className="text-2xl font-serif text-navy mb-6">Send Enquiry</h3>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="inquiry-form">
                <div>
                  <label className="form-label">Market Type *</label>
                  <select value={form.market_type} onChange={e => { const v = e.target.value; setForm(f => ({ ...f, market_type: v })); setType(v); }} className="form-input" required data-testid="market-type-select">
                    <option value="domestic">Domestic (India)</option>
                    <option value="international">International Export</option>
                  </select>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label">Company Name *</label>
                    <div className="relative"><Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" /><input type="text" value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} className="form-input pl-10" placeholder="Your company" required data-testid="company-name-input" /></div>
                  </div>
                  <div>
                    <label className="form-label">Contact Person *</label>
                    <div className="relative"><User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" /><input type="text" value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))} className="form-input pl-10" placeholder="Your name" required data-testid="contact-name-input" /></div>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label">Email *</label>
                    <div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" /><input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="form-input pl-10" placeholder="your@email.com" required data-testid="email-input" /></div>
                  </div>
                  <div>
                    <label className="form-label">Phone *</label>
                    <div className="relative"><Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" /><input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="form-input pl-10" placeholder="+91 98765 43210" required data-testid="phone-input" /></div>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div><label className="form-label">Country *</label><input type="text" value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))} className="form-input" required data-testid="country-input" /></div>
                  <div><label className="form-label">Quantity</label><div className="relative"><Package className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" /><input type="text" value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} className="form-input pl-10" placeholder="e.g., 5000 kg/month" data-testid="quantity-input" /></div></div>
                </div>
                <div>
                  <label className="form-label">Products Interested</label>
                  <div className="grid sm:grid-cols-2 gap-3 mt-2">
                    {products.map(p => (
                      <label key={p.id} className={`flex items-center gap-3 p-3 border cursor-pointer transition-all font-sans text-sm ${form.products.includes(p.name) ? "border-forest bg-forest/5" : "border-slate-200 hover:border-forest/50"}`}>
                        <input type="checkbox" checked={form.products.includes(p.name)} onChange={() => toggleProduct(p.name)} className="w-4 h-4 text-forest rounded" />
                        {p.name}
                      </label>
                    ))}
                  </div>
                </div>
                {form.market_type === "international" && (
                  <div><label className="form-label">Delivery Port</label><input type="text" value={form.delivery_port} onChange={e => setForm(f => ({ ...f, delivery_port: e.target.value }))} className="form-input" placeholder="e.g., Rotterdam" data-testid="delivery-port-input" /></div>
                )}
                <div>
                  <label className="form-label">Payment Terms</label>
                  <select value={form.payment_terms} onChange={e => setForm(f => ({ ...f, payment_terms: e.target.value }))} className="form-input" data-testid="payment-terms-select">
                    <option value="">Select payment terms</option>
                    <option value="advance">Advance Payment</option>
                    <option value="net30">Net 30 Days</option>
                    <option value="net60">Net 60 Days</option>
                    <option value="lc">Letter of Credit</option>
                    <option value="tt">Telegraphic Transfer</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Message</label>
                  <div className="relative"><MessageSquare className="absolute left-3 top-3 w-5 h-5 text-slate-400" /><textarea value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} className="form-input pl-10 min-h-[100px]" placeholder="Tell us about your requirements..." rows={4} data-testid="message-textarea" /></div>
                </div>
                <button type="submit" disabled={submitting} className="w-full btn-primary flex items-center justify-center gap-2 py-4 text-lg" data-testid="submit-inquiry-btn">
                  {submitting ? <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />Submitting...</> : <><Send className="w-5 h-5" />Submit Enquiry</>}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
